/* ══════════════════════════════════════════════════════════════
   pd-table.js — frontend behaviors for the PageBuilder table widget.

   Activated per-table through data attributes the editor sets:
     data-pd-sort="1"    → click/Enter a header cell to sort (numeric-aware)
     data-pd-search="1"  → filters rows from the .pd-table-search input
     data-pd-page="N"    → pagination, N rows per page (pager built here)
     data-pd-export="1"  → CSV download button (label: data-pd-export-label)

   Same lifecycle pattern as html-import-interactions.js: IIFE, run at
   DOMContentLoaded (or immediately), plus a debounced MutationObserver
   so tables appearing after hydration/DOM swaps still initialize.

   Row visibility runs through ONE pipeline: filter (search) → page →
   re-stripe. Hiding is class-only (safe anywhere). Sorting REORDERS
   <tr> nodes and the pager/export INSERT elements — never allowed
   inside a Blazor interactive island (mutating tracked DOM crashes the
   circuit on the next diff), so those are skipped when the table sits
   between `bl:`/`/bl:` island comment markers. Content pages are
   static SSR → everything works there.
   ══════════════════════════════════════════════════════════════ */

(function () {
    'use strict';

    // ── Blazor island detection (comment markers <!--bl:…--> … <!--/bl:…-->) ──
    function insideBlazorIsland(el) {
        var node = el;
        while (node && node !== document.body) {
            var depth = 0;
            var sib = node.previousSibling;
            while (sib) {
                if (sib.nodeType === 8) { // comment
                    var t = (sib.textContent || '').trimStart();
                    if (t.indexOf('/bl:') === 0) depth--;
                    else if (t.indexOf('bl:') === 0) depth++;
                }
                sib = sib.previousSibling;
            }
            if (depth > 0) return true;
            node = node.parentNode;
        }
        return false;
    }

    // ── helpers ─────────────────────────────────────────────────

    function bodyRows(table) {
        var out = [];
        var tbodies = table.tBodies;
        for (var i = 0; i < tbodies.length; i++) {
            var rows = tbodies[i].rows;
            for (var j = 0; j < rows.length; j++) out.push(rows[j]);
        }
        return out;
    }

    function cellText(row, idx) {
        var c = row.cells[idx];
        return c ? (c.textContent || '').trim() : '';
    }

    function toNumber(s) {
        var t = s.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.').replace(/[\u20ac$%]/g, '');
        if (t === '' || isNaN(t)) return null;
        return parseFloat(t);
    }

    function normalize(s) {
        try {
            return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        } catch (e) {
            return s.toLowerCase();
        }
    }

    // ── per-table state + single render pipeline ────────────────

    function makeState(table, staticPage) {
        var st = {
            table: table,
            query: '',
            page: 0,
            pageSize: staticPage ? (parseInt(table.getAttribute('data-pd-page'), 10) || 0) : 0,
            pagerInfo: null,
        };

        st.apply = function () {
            var rows = bodyRows(table);
            // 1. filter
            var matchedCount = 0;
            rows.forEach(function (r) {
                r.__pdMatch = st.query === '' || normalize(r.textContent || '').indexOf(st.query) !== -1;
                if (r.__pdMatch) matchedCount++;
            });
            // 2. page window over the MATCHED rows
            var pages = st.pageSize > 0 ? Math.max(1, Math.ceil(matchedCount / st.pageSize)) : 1;
            if (st.page >= pages) st.page = pages - 1;
            if (st.page < 0) st.page = 0;
            var from = st.pageSize > 0 ? st.page * st.pageSize : 0;
            var to = st.pageSize > 0 ? from + st.pageSize : Infinity;
            var vi = 0;
            rows.forEach(function (r) {
                if (!r.__pdMatch) { r.classList.add('pd-row-hide'); r.classList.remove('pd-row-alt'); return; }
                var inPage = vi >= from && vi < to;
                vi++;
                r.classList.toggle('pd-row-hide', !inPage);
            });
            // 3. stripe (explicit classes — nth-child ignores hidden rows)
            if (table.classList.contains('pd-table--striped')
                || table.classList.contains('pd-table--js-striped')) {
                table.classList.remove('pd-table--striped');
                table.classList.add('pd-table--js-striped');
                var alt = 0;
                rows.forEach(function (r) {
                    if (r.classList.contains('pd-row-hide')) { r.classList.remove('pd-row-alt'); return; }
                    r.classList.toggle('pd-row-alt', alt % 2 === 0);
                    alt++;
                });
            }
            // 4. pager label/buttons
            if (st.pagerInfo) st.pagerInfo(matchedCount, pages);
        };
        return st;
    }

    // ── sorting ─────────────────────────────────────────────────

    function bindSort(table, st) {
        var head = table.tHead;
        if (!head || !head.rows.length) return;
        var ths = head.rows[0].cells;

        function sortBy(idx, dir) {
            var rows = bodyRows(table);
            var numeric = rows.length > 0 && rows.every(function (r) {
                var v = cellText(r, idx);
                return v === '' || toNumber(v) !== null;
            });
            rows.sort(function (a, b) {
                var va = cellText(a, idx), vb = cellText(b, idx);
                var cmp;
                if (numeric) {
                    var na = toNumber(va), nb = toNumber(vb);
                    cmp = (na === null ? -Infinity : na) - (nb === null ? -Infinity : nb);
                } else {
                    cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
                }
                return dir * cmp;
            });
            // re-append in order (stable move — rows keep their tbody)
            rows.forEach(function (r) { r.parentNode.appendChild(r); });
            st.apply();
        }

        function activate(th, idx) {
            var dir = th.classList.contains('pd-sort-asc') ? -1 : 1;
            Array.prototype.forEach.call(ths, function (o) {
                o.classList.remove('pd-sort-asc', 'pd-sort-desc');
                o.removeAttribute('aria-sort');
            });
            th.classList.add(dir === 1 ? 'pd-sort-asc' : 'pd-sort-desc');
            th.setAttribute('aria-sort', dir === 1 ? 'ascending' : 'descending');
            sortBy(idx, dir);
        }

        Array.prototype.forEach.call(ths, function (th, idx) {
            th.tabIndex = 0; // keyboard-sortable
            th.setAttribute('role', 'button');
            th.addEventListener('click', function () { activate(th, idx); });
            th.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    activate(th, idx);
                }
            });
        });
    }

    // ── search / filter ─────────────────────────────────────────

    function bindSearch(table, st) {
        var wrap = table.closest('.pd-table-wrap');
        var input = wrap ? wrap.querySelector('input.pd-table-search') : null;
        if (!input) return;
        input.addEventListener('input', function () {
            st.query = normalize(input.value.trim());
            st.page = 0; // new filter → back to page 1
            st.apply();
        });
    }

    // ── pagination (static pages only — inserts a pager element) ──

    function bindPager(table, st) {
        if (st.pageSize <= 0) return;
        var wrap = table.closest('.pd-table-wrap') || table.parentNode;
        var pager = document.createElement('div');
        pager.className = 'pd-table-pager';
        var prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'pd-table-pager-btn';
        prev.textContent = '‹';
        prev.setAttribute('aria-label', 'Page précédente');
        var label = document.createElement('span');
        label.className = 'pd-table-pager-label';
        var next = document.createElement('button');
        next.type = 'button';
        next.className = 'pd-table-pager-btn';
        next.textContent = '›';
        next.setAttribute('aria-label', 'Page suivante');
        pager.appendChild(prev);
        pager.appendChild(label);
        pager.appendChild(next);
        wrap.appendChild(pager);

        st.pagerInfo = function (total, pages) {
            label.textContent = (st.page + 1) + ' / ' + pages;
            prev.disabled = st.page <= 0;
            next.disabled = st.page >= pages - 1;
            pager.style.display = pages > 1 ? '' : 'none';
        };
        prev.addEventListener('click', function () { if (st.page > 0) { st.page--; st.apply(); } });
        next.addEventListener('click', function () { st.page++; st.apply(); });
    }

    // ── CSV export (static pages only — inserts a button) ───────

    function csvField(s) {
        var t = String(s == null ? '' : s);
        return /[";\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
    }

    function bindExport(table) {
        var wrap = table.closest('.pd-table-wrap') || table.parentNode;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pd-table-export';
        btn.textContent = table.getAttribute('data-pd-export-label') || 'Exporter CSV';
        wrap.appendChild(btn);
        btn.addEventListener('click', function () {
            var lines = [];
            var push = function (row) {
                var cells = [];
                for (var i = 0; i < row.cells.length; i++) {
                    cells.push(csvField((row.cells[i].textContent || '').trim()));
                }
                lines.push(cells.join(';')); // fr-Excel-friendly separator
            };
            if (table.tHead) Array.prototype.forEach.call(table.tHead.rows, push);
            bodyRows(table).forEach(push); // ALL rows, filtered or not
            if (table.tFoot) Array.prototype.forEach.call(table.tFoot.rows, push);
            var blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'tableau.csv';
            document.body.appendChild(a);
            a.click();
            setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 500);
        });
    }

    // ── init ────────────────────────────────────────────────────

    function initTable(table) {
        if (table.__pdTable) return;
        table.__pdTable = true;
        // structural work (row reorder, pager/export insertion) is forbidden
        // inside Blazor islands; class-only features stay available there
        var staticPage = !insideBlazorIsland(table);
        var st = makeState(table, staticPage);
        if (table.hasAttribute('data-pd-search')) bindSearch(table, st);
        if (staticPage) {
            if (table.hasAttribute('data-pd-sort')) bindSort(table, st);
            if (table.hasAttribute('data-pd-page')) bindPager(table, st);
            if (table.hasAttribute('data-pd-export')) bindExport(table);
        }
        if (st.pageSize > 0) st.apply(); // initial page cut
    }

    function scan() {
        var tables = document.querySelectorAll(
            'table[data-pd-sort], table[data-pd-search], table[data-pd-page], table[data-pd-export]');
        for (var i = 0; i < tables.length; i++) initTable(tables[i]);
    }

    var scheduled = null;
    function scheduleScan() {
        if (scheduled) return;
        scheduled = setTimeout(function () { scheduled = null; scan(); }, 300);
    }

    if (document.body) {
        new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scan);
    } else {
        scan();
    }
})();
